import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../navigation/types';
import { useUserSelection } from '../../../contexts/UserSelectionContext';
import { ms, s, vs } from '../../../utils/responsive';

// Temporary mock data - will come from backend later
const MOCK_SUCCESSORS = [
  {
    id: '1',
    name: 'Eleanor Pena',
    email: 'eleanor@gmail.com',
    image: require('../../../assets/avatars/avatar1.png'),
  },
  {
    id: '2',
    name: 'Courtney Henry',
    email: 'courtney@gmail.com',
    image: require('../../../assets/avatars/avatar2.png'),
  },
];

const MOCK_KEY_HOLDERS = [
  {
    id: '3',
    name: 'Leslie Alexander',
    email: 'leslie@gmail.com',
    image: require('../../../assets/avatars/avatar3.png'),
  },
  {
    id: '4',
    name: 'Arlene McCoy',
    email: 'arlebmccoy@gmail.com',
    image: require('../../../assets/avatars/avatar4.png'),
  },
  {
    id: '5',
    name: 'Dianne Russell',
    email: 'dianne@gmail.com',
    image: require('../../../assets/avatars/avatar5.png'),
  },
];

type Contact = {
  id: string;
  name: string;
  email: string;
  image: any;
};

export const PasswordsUnlockedScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const { isViewingOthersData, isDeathVerifiedForSelectedUser, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Passwords require death verification when viewing another user's data
  const requiresDeathVerification = isViewingOthersData && !isDeathVerifiedForSelectedUser;

  const toggleContact = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const renderContact = (contact: Contact) => {
    const isSelected = selectedContacts.includes(contact.id);

    return (
      <TouchableOpacity
        key={contact.id}
        style={styles.contactRow}
        onPress={() => toggleContact(contact.id)}
      >
        <Image source={contact.image} style={styles.contactImage} />
        <View style={styles.contactInfo}>
          <Text style={styles.contactName}>{contact.name}</Text>
          <Text style={styles.contactEmail}>{contact.email}</Text>
        </View>
        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
          {isSelected && (
            <Image
              source={require('../../../assets/icons/check.png')}
              style={styles.checkIcon}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // If death verification is required, show restricted access screen
  if (requiresDeathVerification) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <ScrollView contentContainerStyle={styles.deathVerificationContent}>
          <View style={styles.deathVerificationBanner}>
            <Text style={styles.deathVerificationTitle}>Access Restricted</Text>
            <Text style={styles.deathVerificationText}>
              Password information for {viewingUserName} will only be accessible after a life-ending event has been verified.
            </Text>
            <Text style={styles.deathVerificationSubtext}>
              To gain access, you must submit a death certificate through the app and complete the verification process.
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
      </View>

      <View style={styles.content}>
        <View style={styles.unlockIconContainer}>
          <Image
            source={require('../../../assets/icons/BlueUnlock.png')}
            style={styles.unlockIcon}
            resizeMode="contain"
          />
          <Text style={styles.unlockText}>UNLOCKED</Text>
          <Text style={styles.subtitle}>Select who can view this login information</Text>
        </View>

        <ScrollView style={styles.scrollView}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Successor</Text>
            {MOCK_SUCCESSORS.map(renderContact)}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Key Holder</Text>
            {MOCK_KEY_HOLDERS.map(renderContact)}
          </View>
        </ScrollView>

        <TouchableOpacity
          style={styles.saveButton}
          onPress={() => {
            // Will handle saving selections to backend later
            navigation.navigate('PasswordsDashboard');
          }}
        >
          <Text style={styles.saveButtonText}>Save Changes</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  logo: {
    width: s(80),
    height: s(80),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  unlockIconContainer: {
    alignItems: 'center',
    marginVertical: ms(20),
  },
  unlockIcon: {
    width: s(100),
    height: s(100),
  },
  unlockText: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(10),
  },
  subtitle: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginTop: ms(5),
  },
  scrollView: {
    flex: 1,
    marginTop: ms(20),
  },
  section: {
    marginBottom: ms(30),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: ms(10),
    marginBottom: ms(10),
  },
  contactImage: {
    width: s(50),
    height: s(50),
    borderRadius: ms(25),
  },
  contactInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  contactName: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  contactEmail: {
    fontSize: ms(14, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: 2,
    borderColor: '#1C2C4F',
    marginLeft: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#1C2C4F',
  },
  checkIcon: {
    width: s(14),
    height: s(14),
    tintColor: '#FFF',
  },
  saveButton: {
    backgroundColor: '#00C4CC',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginVertical: ms(20),
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  deathVerificationContent: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: ms(20),
  },
  deathVerificationBanner: {
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#F59E0B',
    borderRadius: ms(12),
    padding: ms(20),
    marginHorizontal: ms(20),
    marginVertical: ms(20),
  },
  deathVerificationTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#92400E',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  deathVerificationText: {
    fontSize: ms(14, 0.3),
    color: '#92400E',
    textAlign: 'center',
    marginBottom: ms(10),
    lineHeight: ms(20, 0.3),
  },
  deathVerificationSubtext: {
    fontSize: ms(12, 0.3),
    color: '#B45309',
    textAlign: 'center',
    lineHeight: ms(18, 0.3),
  },
});
export default PasswordsUnlockedScreen;
